import * as Preferences from "../preference/preference";
import {
  ConfigurationRegistry,
  Configuration as PreferenceConfiguration
} from "../preference/preferenceConfigurationConst";
import { LocalizationService } from "../../ShellCommon/localization/localization";
import { WindowCommunicationBridgeService } from "../../ShellCommon/eventModule/window-communication-bridge";
import { LoggingService } from "./loggingSvc";
import { SectionIdDefaultValue } from "../preference/preferenceController";
import { ServiceDeskConfig } from "../Startup/serviceDeskConfig";
import { SdControllerUtilities } from "./sd-controller/sd-controller-utilities";
import { HttpTrackingConfig } from "../../ShellCommon/logger/http-interceptor";
import { Prerequisite } from "./loading/prerequisite-loading-service";
import { MsalAuthenticationServiceImplementation } from "../../ServiceDesk/sdServices/msal/msal-authentication-service";

/**
 * Object model to index preferences and make accessible client-side
 */
export interface PreferenceModel {
  appId: string;
  sectionId: string;
  content: { [key: string]: string };
}

/**
 * Object model to index preferences and make accessible client-side
 */
export interface PreferenceAppModel {
  appId: string;
  content: { [sectionId: string]: PreferenceModel };
}

interface PreferenceAppCacheModel {
  appId: string;
  sectionId: string;
  cacheModel: PreferenceCacheModel;
}

interface PreferenceCacheModel {
  [key: string]: {
    [key: string]: PreferenceValues;
  };
}
interface PreferenceValues {
  [key: string]: string;
}
/**
 * Define format in which we expect data to be returned from PreferenceService get calls
 */
interface CamelCasedPreferenceModel {
  AppId: string;
  SectionId: string;
  Content: { [key: string]: string };
}

/**
 * Options for get preference operations
 */
export interface GetPreferenceOptions {
  /** If the request should not use the browser cache, but get a fresh copy of the preferences */
  invalidateBrowserCache?: boolean;
}

/**
 * Structure we expect for implementation of PreferenceService
 */
export interface PreferenceApi {
  getPreference(appId: string, sectionId?: string): ng.IPromise<PreferenceModel>;
  getPreferenceSync(appId: string, sectionId?: string): PreferenceModel;
  setPreference(...model: PreferenceModel[]): ng.IPromise<Boolean>;
  deletePreference(appId: string, sectionId?: string, preferenceKey?: string): ng.IPromise<Boolean>;
}
/**
 * Structure we expect for implementation of PreferenceService
 */
export interface PreferenceService extends PreferenceApi {
  /** Gets preferences by an appId asynchronously. */
  getPreferencesByAppId(appId: string): ng.IPromise<PreferenceAppModel>;
  /** Gets preferences by an appId, assuming they've already been loaded. An error will be thrown if the preferences have not been loaded. */
  getPreferencesByAppIdSync(appId: string): PreferenceAppModel;
}

export const preferencesUpdatedEventName = "preferencesUpdatedEvent";
const serviceDeskAppId = "ServiceDesk";
const sdRefreshNeededLogMessage = "RefreshNeededLogMessage";
const cacheKeyPrefix = "Preference";

/**
 * PreferenceService that acts as an intermediary for gets, sets, and deletes of preferences from SD
 */
export class PreferenceService implements PreferenceService {
  private preferencesGetNeeded: any = {};
  private preferencesAppGetNeeded: any = {};
  private preferences: PreferenceCacheModel;
  private cachedGetPreferencePromises: any = [];
  private cachedGetPreferenceByAppIdPromises: any = [];
  private pendingServiceCallMap: Map<string, ng.IPromise<PreferenceModel>> = new Map<
    string,
    ng.IPromise<PreferenceModel>
  >();

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private $q: ng.IQService,
    private preferenceConfigurationRegistry: ConfigurationRegistry,
    private loggingService: LoggingService,
    private $rootScope: ng.IRootScopeService,
    private $injector: ng.auto.IInjectorService,
    private windowCommunicationBridgeService: WindowCommunicationBridgeService,
    private sdControllerUtilities: SdControllerUtilities,
    private sdConfig: ServiceDeskConfig
  ) {
    windowCommunicationBridgeService.subscribe(
      preferencesUpdatedEventName,
      (params: any) => {
        let sectionId = SectionIdDefaultValue;
        // appIdSectionIdPairs is an array of tuple [appId, sectionId]
        for (let appIdSectionIdPair of params.appIdSectionIdPairs) {
          sectionId = appIdSectionIdPair[1] || sectionId;
          this.setPreferencesGetNeeded(appIdSectionIdPair[0], sectionId, true);
          this.getPreference(appIdSectionIdPair[0], sectionId, { invalidateBrowserCache: true });
        }
      },
      $rootScope
    );
  }

  private isPreferencesGetNeeded(appId: string, sectionId: string) {
    if (!this.preferencesGetNeeded[appId]) {
      return true;
    }
    return this.preferencesGetNeeded[appId][sectionId] || this.preferencesGetNeeded[appId][sectionId] == undefined;
  }

  private isPreferencesByAppIdGetNeeded(appId: string) {
    return this.preferencesAppGetNeeded[appId] !== false;
  }

  public getPreferencesByAppId(appId: string): ng.IPromise<PreferenceAppModel> {
    if (!this.isPreferencesByAppIdGetNeeded(appId) && this.cachedGetPreferenceByAppIdPromises[appId]) {
      return this.cachedGetPreferenceByAppIdPromises[appId];
    }

    let getPreferencePromise = this.getPreferencesByAppIdInternal(appId);
    this.cachedGetPreferenceByAppIdPromises[appId] = getPreferencePromise;
    return getPreferencePromise;
  }

  public getPreferencesByAppIdInternal(appId: string): ng.IPromise<PreferenceAppModel> {
    if (!this.isPreferencesByAppIdGetNeeded(appId)) {
      return this.$q.resolve(this.getPreferencesByAppIdSync(appId));
    }
    return this.getConfiguredAppPreferences(appId, { invalidateBrowserCache: true })
      .then((configuredPreferences) => {
        this.setPreferencesByAppIdGetNeeded(appId, false);
        configuredPreferences.forEach((preferenceObject) => {
          this.updatePreferences(
            preferenceObject.appId,
            preferenceObject.sectionId,
            this.createPreferencesFromConfiguredPreferences(
              preferenceObject.cacheModel,
              preferenceObject.appId,
              preferenceObject.sectionId
            )
          );
        });

        return this.$q.resolve(this.getPreferencesByAppIdSync(appId));
      })
      .catch((error) => {
        return this.$q.reject(error);
      });
  }

  getPreferencesByAppIdSync(appId: string): PreferenceAppModel {
    let content = this.preferences[appId] ? this.preferences[appId] : undefined;
    if (!content || this.isPreferencesByAppIdGetNeeded(appId)) {
      throw new Error(`Preference for appId: ${appId} do not exist or have not been loaded.
                Make sure there is a bundle prerequisite for this prior to calling this synchronous method.`);
    }

    let result = <PreferenceAppModel>{ appId: appId, content: {} };
    Object.keys(this.preferences[appId])
      .map((value) => {
        return {
          appId: appId,
          sectionId: value,
          content: content[value]
        };
      })
      .forEach((preferenceModel) => {
        result.content[preferenceModel.sectionId] = preferenceModel;
      });

    return result;
  }

  public setPreferencesGetNeeded(appId: string, sectionId: string, value: boolean) {
    if (!this.preferencesGetNeeded[appId]) {
      this.preferencesGetNeeded[appId] = { [sectionId]: value };
    }
    this.preferencesGetNeeded[appId][sectionId] = value;
    if (value) {
      this.preferencesAppGetNeeded[appId] = value;
    }
  }

  public setPreferencesByAppIdGetNeeded(appId: string, value: boolean) {
    this.preferencesAppGetNeeded[appId] = value;
    let appContainer = this.preferencesGetNeeded[appId];
    if (appContainer) {
      Object.keys(appContainer).forEach((x) => {
        appContainer[x] = value;
      });
    }
  }

  private updatePreferences(appId: string, sectionId: string, values: PreferenceValues) {
    if (!this.preferences) {
      this.preferences = {};
    }
    if (!this.preferences[appId]) {
      this.preferences[appId] = { [sectionId]: values };
    } else {
      this.preferences[appId][sectionId] = values;
    }
  }
  /**
   * Same as GetPreference but synchronous. May get old data if currently fetching.
   * Throws exception if preference not loaded.
   * @param appId
   * @param sectionIdParam
   */
  public getPreferenceSync(appId: string, sectionIdParam?: string): PreferenceModel {
    let sectionId = sectionIdParam;
    if (!sectionId) {
      sectionId = SectionIdDefaultValue;
    }
    let content = this.preferences[appId] ? this.preferences[appId][sectionId] : undefined;
    if (!content) {
      throw new Error(`Preference for appId: ${appId} and section Id: ${sectionIdParam} do not exist or have not been loaded.
                Make sure there is a bundle prerequisite for this prior to calling this synchronous method.`);
    }
    return { appId: appId, sectionId: sectionId, content: content };
  }

  /**
   * Use flight to control if local storage should be used for preference
   * @param appId
   * @param sectionIdParam
   * @param options
   */
  public getPreference(
    appId: string,
    sectionIdParam?: string,
    options: GetPreferenceOptions = {}
  ): ng.IPromise<PreferenceModel> {
    // getPreference is first called by prerequisiteLoadingService when flightingService is not fully loaded.
    // we have decided not to use flight for this feature release due to the concern of adding loading flights as prerequisite
    // could cause performance issue.
    // by calling getPreferenceV2, local storage will be used to store agent preference
    return this.getPreferenceV2(appId, sectionIdParam, options);
  }

  /**
   * Get preference from local storage cache if there is any. Otherwise, get it from service
   * @param appId
   * @param sectionIdParam
   * @param options
   */
  public getPreferenceV2(
    appId: string,
    sectionIdParam?: string,
    options: GetPreferenceOptions = {}
  ): ng.IPromise<PreferenceModel> {
    // let cacheKey: string = sectionIdParam ? `${cacheKeyPrefix}-${appId}-${sectionIdParam}` : `${cacheKeyPrefix}-${appId}`;
    let sectionId = sectionIdParam ? sectionIdParam : SectionIdDefaultValue;
    let cacheKey = `${cacheKeyPrefix}-${appId}-${sectionId}`;
    let preferenceValues: PreferenceValues;

    // get preference from local storage
    // let preferenceCacheStr: string = this.getLocalStorageItem(cacheKey);
    // if (preferenceCacheStr && !this.isPreferencesGetNeeded(appId, sectionId)) {

    // Hard coded to remove http calls
    let preferenceCacheStr: string = "{}";
    if (preferenceCacheStr) {
      try {
        preferenceValues = <PreferenceValues>JSON.parse(preferenceCacheStr);
      } catch (error) {
        let errorMsg = `getPreferenceV2 - Failed on parsing preferenceCacheStr ${preferenceCacheStr}. Error ${error}`;
        this.loggingService.logError(errorMsg, undefined, errorMsg);
      }

      this.updatePreferences(appId, sectionId, preferenceValues);
      return this.$q.resolve({ appId: appId, sectionId: sectionIdParam, content: preferenceValues });
    } else {
      options.invalidateBrowserCache = true;
      // the if block is to prevent extra server calls before local storage item is created
      // more details to this scenario is that before local storage item is created, multiple calls try to get the preference
      // since local storage item is not ready yet, it ends up makeing multiple server calls.
      // get the promise from pendingServiceCallMap map if it already exists
      if (this.pendingServiceCallMap.has(cacheKey)) {
        return this.pendingServiceCallMap.get(cacheKey);
      } else {
        options.invalidateBrowserCache = true;
        // the if block is to prevent extra server calls before local storage item is created
        // more details to this scenario is that before local storage item is created, multiple calls try to get the preference
        // since local storage item is not ready yet, it ends up makeing multiple server calls.
        // get the promise from pendingServiceCallMap map if it already exists
        if (this.pendingServiceCallMap.has(cacheKey)) {
          return this.pendingServiceCallMap.get(cacheKey);
        } else {
          let deferredObj: ng.IDeferred<PreferenceModel> = this.$q.defer();
          this.getConfiguredPreferences(appId, sectionId, options)
            .then((configuredPreferences) => {
              this.setPreferencesGetNeeded(appId, sectionId, false);
              preferenceValues = this.createPreferencesFromConfiguredPreferences(
                configuredPreferences,
                appId,
                sectionId
              );
              if (preferenceValues) {
                this.setLocalStorageItem(cacheKey, preferenceValues);
                // once local storage item is created, delete the pendingServiceCallMap value with the corresponding key
                // otherwise, this.pendingServiceCallMap.get(cacheKey) above will return the unupdated promise when a preference is updated
                if (this.pendingServiceCallMap.has(cacheKey)) {
                  this.pendingServiceCallMap.delete(cacheKey);
                }
                this.updatePreferences(appId, sectionId, preferenceValues);
                deferredObj.resolve({ appId: appId, sectionId: sectionIdParam, content: preferenceValues });
              }
            })
            .catch((error) => {
              deferredObj.reject(error);
            });
          this.pendingServiceCallMap.set(cacheKey, deferredObj.promise);
          return deferredObj.promise;
        }
      }
    }
  }

  /**
   * @param appId
   * @param sectionIdParam
   */
  public getPreferenceV1(
    appId: string,
    sectionIdParam?: string,
    options: GetPreferenceOptions = {}
  ): ng.IPromise<PreferenceModel> {
    if (!this.cachedGetPreferencePromises[appId]) {
      this.cachedGetPreferencePromises[appId] = {};
    }
    let sectionId = sectionIdParam;
    if (!sectionId) {
      sectionId = SectionIdDefaultValue;
    }
    if (!this.isPreferencesGetNeeded(appId, sectionId) && this.cachedGetPreferencePromises[appId][sectionId]) {
      return this.cachedGetPreferencePromises[appId][sectionId];
    }

    let getPreferencePromise = this.getPreferenceInternal(appId, sectionId, options);
    this.cachedGetPreferencePromises[appId][sectionId] = getPreferencePromise;
    return getPreferencePromise;
  }

  private getPreferenceInternal(
    appId: string,
    sectionId: string,
    options: GetPreferenceOptions
  ): ng.IPromise<PreferenceModel> {
    if (!this.getPreferencesFromCacheModel(this.preferences, appId, sectionId)) {
      this.setPreferencesGetNeeded(appId, sectionId, true);
    }
    if (this.isPreferencesGetNeeded(appId, sectionId)) {
      options.invalidateBrowserCache = true;
      this.setPreferencesGetNeeded(appId, sectionId, false);
      return this.getConfiguredPreferences(appId, sectionId, options)
        .then((configuredPreferences) => {
          this.updatePreferences(
            appId,
            sectionId,
            this.createPreferencesFromConfiguredPreferences(configuredPreferences, appId, sectionId)
          );
          return {
            appId: appId,
            sectionId: sectionId,
            content: this.getPreferencesFromCacheModel(this.preferences, appId, sectionId)
          };
        })
        .catch((error) => {
          return this.$q.reject(error);
        });
    } else {
      return this.$q.resolve({
        appId: appId,
        sectionId: sectionId,
        content: this.getPreferencesFromCacheModel(this.preferences, appId, sectionId)
      });
    }
  }

  private async getPreferencesHttp(url: string, options: GetPreferenceOptions) {
    var cachedTokenObject = await this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes);
    var cachedToken: string = "null";
    if (cachedTokenObject) {
      cachedToken = cachedTokenObject;
    }

    let config: HttpTrackingConfig = {
      headers: {
        Authorization: "bearer " + cachedToken,
        "Content-Type": "application/json"
      },
      dependencyName: "ServiceDesk.SDController",
      operationName: "Preference.Get",
      dependencyOperationName: "Preference.Get"
    };
    if (options.invalidateBrowserCache) {
      config.headers["Cache-Control"] = "no-cache";
      config.headers["Pragma"] = (config.headers["Pragma"] || "") + "no-cache";
    }

    angular.merge(config, this.sdControllerUtilities.getParametersForUserCacheBusting());

    return this.$http.get(url, config);
  }

  private processGetPreferencesResponse(
    data: CamelCasedPreferenceModel,
    appId: string,
    sectionId: string
  ): PreferenceCacheModel {
    let contentArr: { [key: string]: string } = {};
    if (data) {
      for (let key in data.Content) {
        if (data.Content.hasOwnProperty(key)) {
          contentArr[key] = data.Content[key];
        }
      }
    }
    return {
      [appId]: {
        [sectionId]: contentArr
      }
    };
  }

  private getConfiguredAppPreferences(
    appId: string,
    options: GetPreferenceOptions
  ): ng.IPromise<PreferenceAppCacheModel[]> {
    let url = this.sdConfig.sdBaseUrl + "customer/api/Preference/" + appId + "/";
    return this.getPreferencesHttp(url, options).then(
      (arg: ng.IHttpPromiseCallbackArg<CamelCasedPreferenceModel[]>) => {
        return (arg.data || []).map((data: CamelCasedPreferenceModel) => {
          return {
            appId: data.AppId,
            sectionId: data.SectionId,
            cacheModel: this.processGetPreferencesResponse(data, data.AppId, data.SectionId)
          };
        });
      },
      (error) => {
        this.loggingService.logError(error);
        return this.$q.reject(error);
      }
    );
  }

  private getConfiguredPreferences(
    appId: string,
    sectionId: string,
    options: GetPreferenceOptions
  ): ng.IPromise<PreferenceCacheModel> {
    let url = this.sdConfig.sdBaseUrl + "customer/api/Preference/" + appId + "/" + sectionId;
    return this.getPreferencesHttp(url, options).then(
      (arg: ng.IHttpPromiseCallbackArg<CamelCasedPreferenceModel>) => {
        let data: any = arg.data || {};
        if (data instanceof Array) {
          data = data[0];
        }
        return this.processGetPreferencesResponse(data, appId, sectionId);
      },
      (error) => {
        this.setPreferencesGetNeeded(appId, sectionId, true);
        this.loggingService.logError(error);
        return this.$q.reject(error);
      }
    );
  }

  private createPreferencesFromConfiguredPreferences(
    preferences: PreferenceCacheModel,
    appId: string,
    sectionId: string
  ): PreferenceValues {
    let preferenceValuesWithInitialValues = angular.copy(preferences[appId][sectionId]);
    let filteredConfig: PreferenceConfiguration[] = [];
    filteredConfig = this.preferenceConfigurationRegistry.configurations.filter(function (
      configuration: PreferenceConfiguration
    ) {
      return (
        configuration.sectionConfiguration.appId == appId && configuration.sectionConfiguration.sectionId == sectionId
      );
    });

    if (filteredConfig.length > 0) {
      filteredConfig.forEach((filteredConfigItem) => {
        if (filteredConfigItem && filteredConfigItem.preferenceElements) {
          filteredConfigItem.preferenceElements.forEach((currentElement: any) => {
            if (currentElement instanceof Preferences.InteractablePreferenceElement) {
              if (currentElement.preferenceKey) {
                let preferenceValue = preferenceValuesWithInitialValues[currentElement.preferenceKey];
                if (
                  currentElement.initialValue !== undefined &&
                  currentElement.initialValue != "" &&
                  preferenceValue == undefined
                ) {
                  preferenceValue = currentElement.initialValue.toString();
                }
                let preferences = this.getPreferencesFromCacheModel(this.preferences, appId, sectionId);
                if (currentElement.preventMidSessionUpdate && preferences) {
                  let existingValue = preferences[currentElement.preferenceKey];
                  if (existingValue != preferenceValue) {
                    preferenceValue = existingValue;
                    this.showRefreshPageInfo();
                  }
                }
                preferenceValuesWithInitialValues[currentElement.preferenceKey] = preferenceValue;
              }
            }
          });
        }
      });
    }
    return preferenceValuesWithInitialValues;
  }

  private getPreferencesFromCacheModel(
    preferences: PreferenceCacheModel,
    appId: string,
    sectionId: string
  ): { [key: string]: string } {
    if (preferences && preferences[appId]) {
      return preferences[appId][sectionId];
    }
    return undefined;
  }

  private showRefreshPageInfo() {
    let localizationService = <LocalizationService>this.$injector.get("sdLocalizationService");
    let refreshNeededLocalizedButtonText = localizationService.getLocalizedString(
      serviceDeskAppId,
      "_SDSERVICES_PREFERENCE_REFRESH_NEEDED_BUTTON_TEXT_"
    );
    let refreshNeededLocalizedMessage = localizationService.getLocalizedString(
      serviceDeskAppId,
      "_SDSERVICES_PREFERENCE_REFRESH_NEEDED_MESSAGE_"
    );
    this.loggingService.logInfo("Preference changes require refresh", {
      displayText: refreshNeededLocalizedMessage,
      uId: sdRefreshNeededLogMessage,
      alertActions: [
        {
          linkText: refreshNeededLocalizedButtonText,
          callback: () => {
            location.reload();
          },
          biId: "refreshAfterPreferenceUpdateNotificationButton"
        }
      ]
    });
  }

  public setPreference(...model: PreferenceModel[]): ng.IPromise<Boolean> {
    let camelCasedModels: CamelCasedPreferenceModel[] = [];
    if (!model) {
      throw new Error("Invalid or no content");
    } else {
      for (let index = 0; index < model.length; index++) {
        camelCasedModels.push({
          AppId: model[index].appId,
          SectionId: model[index].sectionId || SectionIdDefaultValue,
          Content: model[index].content
        });
      }
    }
    let url = this.sdConfig.sdBaseUrl + "customer/api/Preference/PostMultiple";

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }
      return this.$http
        .post(url, camelCasedModels, <HttpTrackingConfig>{
          headers: {
            Authorization: "bearer " + cachedToken,
            "Content-Type": "application/json"
          },
          dependencyName: "ServiceDesk.SDController",
          operationName: "Preference.Post",
          dependencyOperationName: "Preference.Post"
        })
        .then(
          (data: any) => {
            // appIdSectionIdPairs is an array of tuple [appId, sectionId]
            let appIdSectionIdPairs: [string, string][] = [];
            for (let index = 0; index < model.length; index++) {
              this.setPreferencesGetNeeded(model[index].appId, model[index].sectionId, true);
              appIdSectionIdPairs.push([model[index].appId, model[index].sectionId]);
            }
            this.windowCommunicationBridgeService.publish(preferencesUpdatedEventName, {
              appIdSectionIdPairs: appIdSectionIdPairs
            });
            return true;
          },
          (error: any) => {
            this.loggingService.logError(error);
            return false;
          }
        );
    });
  }

  public deletePreference(appId: string, sectionId?: string, preferenceKey?: string): ng.IPromise<Boolean> {
    let url = this.sdConfig.sdBaseUrl + "customer/api/Preference";
    if (appId) {
      url += "/" + appId + "/" + (sectionId || SectionIdDefaultValue);
    } else {
      throw new Error("Invalid or wrong parameters");
    }

    if (preferenceKey) {
      url += "/" + preferenceKey;
    }

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }
      return this.$http
        .delete(url, <HttpTrackingConfig>{
          headers: {
            Authorization: "bearer " + cachedToken
          },
          dependencyName: "ServiceDesk.SDController",
          operationName: "Preference.Delete",
          dependencyOperationName: "Preference.Delete"
        })
        .then(
          (data: any) => {
            this.setPreferencesGetNeeded(appId, sectionId, true);
            let cacheKey: string = sectionId ? `${cacheKeyPrefix}-${appId}-${sectionId}` : `${cacheKeyPrefix}-${appId}`;
            this.removeLocalStorageItem(cacheKey);
            return true;
          },
          (error: any) => {
            this.loggingService.logError(error);
            return false;
          }
        );
    });
  }

  /**
   * Delete all the preference data and knowledge favorite data
   */
  public deleteAllPreference(): ng.IPromise<Boolean> {
    let oid = this.msalAuthenticationService.userInfo.idToken.oid;
    let preferenceUrl = this.sdConfig.sdBaseUrl + "customer/api/Preference";
    let knowledgeFavoritesUrl = this.sdConfig.sdBaseUrl + "SDController/api/KnowledgeFavorites/" + oid;

    return this.msalAuthenticationService._getCachedToken(this.sdConfig.msalScopes).then(function (token) {
      var cachedToken: string = "null";
      if (token) {
        cachedToken = token;
      }

      let preferencePromise = this.$http.delete(preferenceUrl, <HttpTrackingConfig>{
        headers: {
          Authorization: "bearer " + cachedToken
        },
        dependencyName: "ServiceDesk.SDController",
        operationName: "Preference.Delete",
        dependencyOperationName: "Preference.Delete"
      });

      this.deletePreferenceLocalStorageItems();
      let knowledgePromise = this.$http.delete(knowledgeFavoritesUrl, <HttpTrackingConfig>{
        headers: {
          Authorization: "bearer " + cachedToken
        },
        dependencyName: "ServiceDesk.SDController",
        operationName: "KnowledgeFavorites.Delete",
        dependencyOperationName: "KnowledgeFavorites.Delete"
      });
      return this.$q
        .all([preferencePromise, knowledgePromise])
        .then(() => {
          return true;
        })
        .catch((error: any) => {
          this.loggingService.logError("Delete all preference failed.", error);
          return false;
        });
    });
  }

  private deletePreferenceLocalStorageItems() {
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        let key: string = window.localStorage.key(i);
        if (key.substr(0, cacheKeyPrefix.length) === cacheKeyPrefix) {
          window.localStorage.removeItem(key);
        }
      }
    } catch (error) {
      let errorMsg = `deletePreferenceLocalStorage - Failed on deleting preference local storage items. Error: ${error}`;
      this.loggingService.logWarning(errorMsg);
    }
  }

  private removeLocalStorageItem(cacheKey: string) {
    try {
      window.localStorage.removeItem(cacheKey);
    } catch (error) {
      let errorMsg = `removeLocalStorageItem - Failed on deleting preference local storage item cacheKey ${cacheKey}. Error: ${error}`;
      this.loggingService.logWarning(errorMsg);
    }
  }

  private getLocalStorageItem(cacheKey: string): string {
    try {
      return window.localStorage.getItem(cacheKey);
    } catch (error) {
      let errorMsg = `getLocalStorageItem - Failed on getting preference local storage item cacheKey ${cacheKey}. Error: ${error}`;
      this.loggingService.logWarning(errorMsg);
    }
  }

  private setLocalStorageItem(cacheKey: string, preferenceValues: PreferenceValues) {
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(preferenceValues));
    } catch (error) {
      let errorMsg = `setLocalStorageItem - Failed on setting preference local storage item cacheKey ${cacheKey}. Error: ${error}`;
      this.loggingService.logWarning(errorMsg);
    }
  }

  private verifyPreferenceObject(object: any) {
    if (!angular.isArray(object)) {
      throw new Error(`Preferences object should be an array ${JSON.stringify(object)}`);
    }
    let foundBadPreference = object.find((preference: { appId: string; sectionId: string }) => {
      return (
        typeof preference.appId != "string" ||
        (preference.sectionId != undefined && typeof preference.sectionId != "string") ||
        Object.keys(preference).length > 2
      );
    });
    if (foundBadPreference) {
      throw new Error(`Provided preference object cannot be cast to preference ${JSON.stringify(object)}`);
    }
  }

  public prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    if (!prerequisite.preferences) {
      return this.$q.resolve();
    }
    this.verifyPreferenceObject(prerequisite.preferences);
    let alreadyProcessedPreferences = new Set<string>();

    let allPromises = prerequisite.preferences.map((preferenceModel: any) => {
      let preferenceKey = preferenceModel.appId + preferenceModel.sectionId;
      if (!alreadyProcessedPreferences.has(preferenceKey)) {
        alreadyProcessedPreferences.add(preferenceKey);
        return this.getPreference(
          preferenceModel.appId,
          preferenceModel.sectionId == "" ? undefined : preferenceModel.sectionId
        );
      }
    });
    return this.$q.all(allPromises).finally(() => {
      return this.$q.resolve();
    });
  }
}
